import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Button, Modal, Form, Spinner, Container, Row, Col, Alert, Pagination } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  getQuestionSets_user as getQuestionSets,
  createQuestionSet_user as createQuestionSet,
  updateQuestionSet_user as updateQuestionSet,
  deleteQuestionSet_user as deleteQuestionSet
} from './../api';

const QuestionSets = () => {
  const title = "List of Query Sets";
  const [sets, setSets] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSet, setSelectedSet] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [setsPerPage] = useState(10);
  const [alert, setAlert] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const navigate = useNavigate();

  const sortableHeaderStyle = {
    cursor: 'pointer',
    userSelect: 'none',
    position: 'relative',
    paddingRight: '20px'
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const sortData = (data, field, direction) => {
    return [...data].sort((a, b) => {
      let aVal = a[field];
      let bVal = b[field];

      switch (field) {
        case 'created_at':
          aVal = new Date(aVal);
          bVal = new Date(bVal);
          break;
        case 'questions':
          aVal = aVal?.length || 0;
          bVal = bVal?.length || 0;
          break;
        case 'category':
        case 'user_id':
          aVal = aVal?.toString().toLowerCase();
          bVal = bVal?.toString().toLowerCase();
          break;
        default:
          break;
      }

      if (direction === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
  };

  const handleSort = (field) => {
    const newDirection = field === sortField && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
  };

  const fetchSets = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getQuestionSets();
      setSets(response.data);
    } catch (error) {
      console.error('Error:', error);
      setAlert({ variant: 'danger', message: 'Failed to fetch question sets' });
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchSets();
  }, [fetchSets]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {
      category: formData.get('category'),
      description: formData.get('description'),
      is_global: formData.get('is_global') === 'on'
    };

    try {
      if (selectedSet) {
        await updateQuestionSet(selectedSet.id, data);
        setAlert({ variant: 'success', message: 'Question set updated successfully' });
      } else {
        await createQuestionSet(data);
        setAlert({ variant: 'success', message: 'Question set created successfully' });
      }
      fetchSets();
      setShowModal(false);
      setSelectedSet(null);
    } catch (error) {
      console.error('Error:', error);
      setAlert({ variant: 'danger', message: 'Failed to save question set' });
    }
  };

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
    setSelectedSet(null);
  };

  const handleDelete = async (setId) => {
    try {
      await deleteQuestionSet(setId);
      fetchSets();
      handleDeleteClose();
      setAlert({ variant: 'success', message: 'Question set deleted successfully' });
    } catch (error) {
      console.error('Error:', error);
      setAlert({ variant: 'danger', message: 'Failed to delete question set' });
    }
  };

  const filteredAndSortedSets = useMemo(() => {
    let result = sets.filter(set =>
      set.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      set.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (sortField) {
      result = sortData(result, sortField, sortDirection);
    }

    return result;
  }, [sets, searchTerm, sortField, sortDirection]);

  const indexOfLastSet = currentPage * setsPerPage;
  const indexOfFirstSet = indexOfLastSet - setsPerPage;
  const currentSets = filteredAndSortedSets.slice(indexOfFirstSet, indexOfLastSet);
  const totalPages = Math.ceil(filteredAndSortedSets.length / setsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const pageNumbers = [];
  for (let i = 1; i <= Math.ceil(filteredAndSortedSets.length / setsPerPage); i++) {
    pageNumbers.push(i);
  }

  if (isLoading) {
    return <div className="text-center mt-5"><Spinner animation="border" /></div>;
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className="container-fluid mb-5 px-3">
        <div className="row g-5 pt-3">
          <div className="col-12">
            <div className="card">
              <div className="card-header pt-3 bg-white">

                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h4 className="text-secondary">Question Sets</h4>
                  </div>

                </div>
              </div>
              <div className="card-header d-flex justify-content-between align-items-center">
                <div>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Query Set Title Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: '300px' }}
                  />
                </div>
                <div>
                  <button type="button" className="btn btn-outline-primary" onClick={() => setShowModal(true)}>
                    {/*  <i className="bi bi-plus-circle me-1"></i> */}
                    New Query Set</button>
                </div>
              </div>
              <div className="table-responsive position-relative">
                <div className="mb-2">
                  (To Sort by the Information Column, Please Click on the Column Header)
                </div>
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>#</th>
                      {[
                        { field: 'category', label: 'Title' },
                        { field: 'user_id', label: 'Creator' },
                        { field: 'created_at', label: 'Created Date' },
                        { field: 'questions', label: 'Number of Questions' },
                        /*  { field: 'is_global', label: 'Global Use' } */
                      ].map(({ field, label }) => (
                        <th
                          key={field}
                          onClick={() => handleSort(field)}
                          style={sortableHeaderStyle}
                          className="position-relative"
                        >
                          {label}
                          {getSortIcon(field) && (
                            <span className="position-absolute" style={{ right: '5px' }}>
                              {getSortIcon(field)}
                            </span>
                          )}
                        </th>
                      ))}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentSets.map((set, index) => (
                      <tr key={set.id}>
                        <td>{indexOfFirstSet + index + 1}</td>
                        <td onClick={() => navigate(`/console/questionsets/${set.id}`)}
                          style={{ cursor: 'pointer', color: '#007bff' }}>
                          {set.category}
                        </td>
                        <td>{set.user_id}</td>
                        <td>{new Date(set.created_at).toLocaleDateString()}</td>
                        <td>{set.questions?.length || 0}</td>
                        {/*                         <td>
                          <Form.Check
                            type="checkbox"
                            checked={set.is_global || false}
                            onChange={async (e) => {
                              try {
                                await updateQuestionSet(set.id, {
                                  ...set,
                                  is_global: e.target.checked
                                });
                                fetchSets();
                                setAlert({ variant: 'success', message: 'Global use status updated successfully' });
                              } catch (error) {
                                console.error('Error:', error);
                                setAlert({ variant: 'danger', message: 'Failed to update global use status' });
                              }
                            }}
                          />
                        </td> */}
                        <td>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => {
                                setSelectedSet(set);
                                setShowModal(true);
                              }}
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => {
                                setSelectedSet(set);
                                setShowDeleteModal(true);
                              }}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <div className="card-footer d-flex justify-content-between align-items-center bg-white">
                  <div>
                    <Pagination>
                      {pageNumbers.map(number => (
                        <Pagination.Item key={number} active={number === currentPage} onClick={() => paginate(number)}>
                          {number}
                        </Pagination.Item>
                      ))}
                    </Pagination>
                  </div>
                </div>

              </div>
            </div>
          </div>
          <div>
            {alert && (
              <Alert variant={alert.variant} onClose={() => setAlert(null)} dismissible>
                {alert.message}
              </Alert>
            )}
          </div>
        </div >
      </div >

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => {
        setShowModal(false);
        setSelectedSet(null);
      }} centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{selectedSet ? 'Edit' : 'Add'} Question Set</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                name="category"
                defaultValue={selectedSet?.category}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                defaultValue={selectedSet?.description}
                rows={3}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="global-use"
                name="is_global"
                label="Global Use"
                defaultChecked={selectedSet?.is_global}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setShowModal(false);
              setSelectedSet(null);
            }}>Cancel</Button>
            <Button variant="primary" type="submit">Save</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this question set "{selectedSet?.category}"?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => handleDelete(selectedSet?.id)}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

    </>
  );
};

export default QuestionSets;